import { Component, OnInit, Input, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { CoreService } from '../../../../services/core.service';
import { SectionGridComponent } from '../../components/section-grid/section-grid.component';
import { FooterGridComponent } from '../../components/footer-grid/footer-grid.component';

@Component({
  selector: 'app-edit-platform',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    SectionGridComponent,
    FooterGridComponent
  ],
  templateUrl: './edit-platform.component.html',
  styleUrl: './edit-platform.component.scss'
})
export class EditPlatformComponent implements OnInit, OnDestroy {
  @Input() platformID!: number;
  @Input() siteID!: number;
  @Input() embeddedMode = false;
  @Input() hasBorder = true;
  @Input() companyID!: number;

  platformForm: FormGroup;
  isSubmitting = false;
  isLoading = true;

  // Inline messages
  successMessage = '';
  errorMessage = '';

  // Site options for dropdown
  siteOptions: any[] = [];
  hasDefaultSite = false;

  // Prompt visibility flags
  showPromptField = true;
  showFbPrompts = false;
  showIgPrompts = false;
  showTikTokPrompts = false;

  constructor(
    private fb: FormBuilder,
    private coreService: CoreService
  ) {
    this.platformForm = this.createForm();
  }

  ngOnInit(): void {
    this.loadPlatformData();
    this.loadSiteOptions();
  }

  ngOnDestroy(): void {
    // nothing to clean up now related to messages
  }

  createForm(): FormGroup {
    return this.fb.group({
      socialPlatform: ['', Validators.required],
      siteID: [''],
      socialMediaID: ['', Validators.required],
      accessToken: ['', [Validators.required, Validators.minLength(190)]],
      appID: ['', [Validators.required, Validators.minLength(1)]],
      appSecret: ['', [Validators.required, Validators.minLength(1)]],
      fbDefaultPrompt: [''],
      fbAlternativePrompt: [''],
      igDefaultPrompt: [''],
      igAlternativePrompt: [''],
      tikTokDefaultPrompt: [''],
      tikTokAlternativePrompt: ['']
    });
  }

  loadPlatformData(): void {
    // hard-coded IDs for testing
    this.platformID = this.platformID || 5;
    this.siteID = this.siteID || 32;
    this.companyID = this.companyID || 1;

    if (!this.platformID) {
      this.isLoading = false;
      return;
    }

    this.isLoading = true;

    this.coreService
      .getData(`/manageAccount/platform/edit/${this.platformID}`, {
        companyID: this.companyID
      })
      .subscribe({
        next: (response: any) => {
          const platformData = response.data || response;
          console.log('Platform data loaded:', platformData);

          this.platformForm.patchValue({
            socialPlatform:
              platformData.socialPlatform || platformData.platform || '',
            siteID:
              platformData.siteID || platformData.ms_contentsite_id || '',
            socialMediaID:
              platformData.socialMediaID || platformData.social_media_id || '',
            accessToken: platformData.accessToken || platformData.access_token || '',
            appID: platformData.appID || platformData.app_id || '',
            appSecret: platformData.appSecret || platformData.app_secret || '',
            fbDefaultPrompt:
              platformData.fbDefaultPrompt ||
              platformData.fb_default_prompt ||
              '',
            fbAlternativePrompt:
              platformData.fbAlternativePrompt ||
              platformData.fb_alternative_prompt ||
              '',
            igDefaultPrompt:
              platformData.igDefaultPrompt ||
              platformData.ig_default_prompt ||
              '',
            igAlternativePrompt:
              platformData.igAlternativePrompt ||
              platformData.ig_alternative_prompt ||
              '',
            tikTokDefaultPrompt:
              platformData.tikTokDefaultPrompt ||
              platformData.tiktok_default_prompt ||
              '',
            tikTokAlternativePrompt:
              platformData.tikTokAlternativePrompt ||
              platformData.tiktok_alternative_prompt ||
              ''
          });

          const platform =
            platformData.socialPlatform || platformData.platform || '';
          this.updatePromptVisibility(platform);

          const siteIDVal =
            platformData.siteID || platformData.ms_contentsite_id;
          this.checkDefaultSite(siteIDVal);

          this.isLoading = false;
        },
        error: (error) => {
          console.error('Error loading platform data:', error);
          this.errorMessage =
            error.error?.message ||
            'Failed to load platform data. Please try again.';
          this.isLoading = false;
        }
      });
  }

  loadSiteOptions(): void {
    this.coreService
      .getData('/manageAccount/sites/index', { companyID: this.companyID })
      .subscribe({
        next: (response: any) => {
          this.siteOptions = response.data || response || [];
          console.log('Site options loaded:', this.siteOptions);
        },
        error: (error) => {
          console.error('Error loading site options:', error);
          this.siteOptions = [];
        }
      });
  }

  updatePromptVisibility(platform: string): void {
    this.showFbPrompts = false;
    this.showIgPrompts = false;
    this.showTikTokPrompts = false;

    switch (platform) {
      case 'Facebook':
        this.showFbPrompts = true;
        break;
      case 'Instagram':
        this.showIgPrompts = true;
        break;
      case 'TikTok':
        this.showTikTokPrompts = true;
        break;
    }
  }

  checkDefaultSite(siteID: number): void {
    const selectedSite = this.siteOptions.find((site) => site.ID === siteID);
    this.hasDefaultSite = selectedSite
      ? selectedSite.Domain === 'default' ||
        selectedSite.siteUrl === 'default'
      : false;
  }

  onPlatformChange(): void {
    const platform = this.platformForm.get('socialPlatform')?.value;
    this.updatePromptVisibility(platform);
  }

  onSubmit(): void {
    if (this.platformForm.invalid) {
      this.markFormGroupTouched(this.platformForm);
      return;
    }

    this.isSubmitting = true;
    this.successMessage = '';
    this.errorMessage = '';

    const formData = new FormData();
    const formValues = this.platformForm.value;

    formData.append('id', this.platformID.toString());
    formData.append('companyID', this.companyID.toString());

    // Map new control names to backend field names
    formData.append('socialPlatform', formValues.socialPlatform || '');
    formData.append('siteID', formValues.siteID || '');
    formData.append('socialMediaID', formValues.socialMediaID || '');
    formData.append('accessToken', formValues.accessToken || '');
    formData.append('appID', formValues.appID || '');
    formData.append('appSecret', formValues.appSecret || '');

    if (formValues.socialPlatform === 'Facebook') {
      formData.append('fbDefaultPrompt', formValues.fbDefaultPrompt || '');
      formData.append(
        'fbAlternativePrompt',
        formValues.fbAlternativePrompt || ''
      );
    } else if (formValues.socialPlatform === 'Instagram') {
      formData.append('igDefaultPrompt', formValues.igDefaultPrompt || '');
      formData.append(
        'igAlternativePrompt',
        formValues.igAlternativePrompt || ''
      );
    } else if (formValues.socialPlatform === 'TikTok') {
      formData.append('tikTokDefaultPrompt', formValues.tikTokDefaultPrompt || '');
      formData.append(
        'tikTokAlternativePrompt',
        formValues.tikTokAlternativePrompt || ''
      );
    }

    console.log('FormData entries being sent:');
    for (let pair of (formData as any).entries()) {
      console.log(pair[0] + ': ', pair[1]);
    }

    this.coreService
      .postData(`manageAccount/platform/update/${this.platformID}`, formData)
      .subscribe({
        next: (response: any) => {
          console.log('Platform updated successfully:', response);
          this.successMessage = 'Platform updated successfully!';
        },
        error: (error) => {
          console.error('Error updating platform:', error);
          this.errorMessage =
            error.error?.message ||
            'Failed to update platform. Please try again.';
        },
        complete: () => {
          this.isSubmitting = false;
        }
      });
  }

  onCancel(): void {
    console.log('Edit cancelled');
  }

  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach((control) => {
      control.markAsTouched();
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      }
    });
  }

  getControl(controlName: string) {
    return this.platformForm.get(controlName);
  }
}
